import React, { useEffect, useState } from 'react';
import { Card } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { Navigate } from 'react-router-dom';
import { authStore } from '../stores/authStore';

interface SystemInfo {
  version: string;
  environment: string;
  region: string;
  apiEndpoint: string;
  buildDate: string;
  commitHash: string;
  nodeVersion: string;
  npmVersion: string;
}

interface ConfigInfo {
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  region: string;
}

const SystemInfoPage: React.FC = observer(() => {
  const [systemInfo] = useState<SystemInfo>({
    version: '1.0.0',
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    region: import.meta.env.VITE_AWS_REGION || 'ap-northeast-1',
    apiEndpoint: import.meta.env.VITE_API_URL || 'N/A',
    buildDate: new Date().toISOString(),
    commitHash: 'N/A',
    nodeVersion: 'N/A',
    npmVersion: 'N/A',
  });

  const [serverInfo, setServerInfo] = useState<any>(null);
  const [configInfo, setConfigInfo] = useState<ConfigInfo | null>(null);
  const [loading, setLoading] = useState(false);

  // rootテナント以外はアクセス不可
  if (!authStore.isRootAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

    // Config情報を取得
    const fetchConfigInfo = async () => {
      try {
        const response = await fetch(`${apiUrl}/v1/config`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setConfigInfo(data);
      } catch (error) {
        console.error('Failed to fetch config info:', error);
      }
    };

    // サーバー情報を取得
    const fetchServerInfo = async () => {
      setLoading(true);
      try {
        const token = await authStore.getIdToken();

        const response = await fetch(`${apiUrl}/v1/root/system-info`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setServerInfo(data);
      } catch (error) {
        console.error('Failed to fetch server info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigInfo();
    fetchServerInfo();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          システム情報
        </h1>
        <p className="text-gray-600">システムの詳細情報と稼働状況</p>
      </div>

      {/* 基本情報 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">基本情報</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">バージョン</p>
            <p className="font-medium">{systemInfo.version}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">環境</p>
            <p className="font-medium capitalize">{systemInfo.environment}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">リージョン</p>
            <p className="font-medium">{systemInfo.region} (東京)</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">APIエンドポイント</p>
            <p className="font-medium break-all">{systemInfo.apiEndpoint}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">ビルド日時</p>
            <p className="font-medium">{new Date(systemInfo.buildDate).toLocaleString('ja-JP')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">最終更新</p>
            <p className="font-medium">{new Date().toLocaleString('ja-JP')}</p>
          </div>
        </div>
      </Card>

      {/* データベース統計 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">データベース統計</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded">
            <p className="text-3xl font-bold text-blue-600">
              {loading ? '...' : serverInfo?.database?.tenantCount || '0'}
            </p>
            <p className="text-sm text-gray-600 mt-1">テナント数</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded">
            <p className="text-3xl font-bold text-green-600">
              {loading ? '...' : serverInfo?.database?.userCount || '0'}
            </p>
            <p className="text-sm text-gray-600 mt-1">ユーザー数</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded">
            <p className="text-3xl font-bold text-purple-600">
              {loading ? '...' : serverInfo?.database?.registrationCount || '0'}
            </p>
            <p className="text-sm text-gray-600 mt-1">登録数（累計）</p>
          </div>
        </div>
      </Card>

      {/* AWS リソース情報 */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">AWS リソース</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Cognito ユーザープール</p>
            <p className="font-medium break-all">
              {loading ? '読み込み中...' : configInfo?.userPoolId || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Cognito クライアントID</p>
            <p className="font-medium break-all">
              {loading ? '読み込み中...' : configInfo?.userPoolClientId || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Cognito Identity Pool ID</p>
            <p className="font-medium break-all">
              {loading ? '読み込み中...' : configInfo?.identityPoolId || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">DynamoDB テーブル接頭辞</p>
            <p className="font-medium">
              med-well-call-{systemInfo.environment}
            </p>
          </div>
        </div>
      </Card>

      {/* サーバー情報 */}
      {serverInfo && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">サーバー情報</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Goバージョン</p>
              <p className="font-medium">{serverInfo.runtime?.goVersion || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">CPU数</p>
              <p className="font-medium">{serverInfo.runtime?.numCpu || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">稼働時間</p>
              <p className="font-medium">{serverInfo.system?.uptime || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">メモリ使用量</p>
              <p className="font-medium">
                {serverInfo.runtime?.memoryAllocated
                  ? `${(serverInfo.runtime.memoryAllocated / 1024 / 1024).toFixed(2)} MB`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
});

export default SystemInfoPage;